'use client';

import Markdown, { type Components } from 'react-markdown';

import { IconName, Link, Text, TypographyBold } from '@bytel/trilogy-react';

import { FaiType } from '@app-types/plan';

import { NuggetsList } from '@components/nuggets-list';

import { getSpeedInfos } from '@helpers/speed-infos';

interface FaiNuggetsListProps {
    plan: FaiType;
    displaySpeed?: boolean;
}

type FaiNugget = NonNullable<FaiType['nuggets']>[number];

const markdownComponents: Components = {
    p: 'span',
    a: ({ children, href }) => (
        <Link href={href} blank>
            {children}
        </Link>
    ),
};

function getNuggetKey(nugget: FaiNugget, index: number): string {
    if (typeof nugget.position === 'number') {
        return ['fai', nugget.position].join('-');
    }

    return ['fai', nugget.icon ?? nugget.image ?? 'nugget', index].join('-');
}

export function FaiNuggetsList({ plan, displaySpeed = true }: Readonly<FaiNuggetsListProps>) {
    const nuggets = (plan.nuggets ?? [])
        .filter((nugget) => displaySpeed || nugget.showSpeed !== true)
        .toSorted((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));
    const speed = displaySpeed && nuggets.some((nugget) => nugget.showSpeed) ? getSpeedInfos(plan) : undefined;

    return (
        <NuggetsList
            nuggets={nuggets.map((nugget, index) => ({
                key: getNuggetKey(nugget, index),
                ...(nugget.image
                    ? { image: nugget.image }
                    : { icon: (nugget.icon as IconName | undefined) ?? IconName.CHECK }),
                content: (
                    <>
                        <Markdown components={markdownComponents}>{nugget.label}</Markdown>
                        {nugget.showSpeed && speed && (
                            <>
                                <br />
                                {speed.speedWording && ` ${speed.speedWording}`}
                                <Text
                                    markup='span'
                                    typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                    accessibilityLabel={`${speed.downRates} en débit descendant et ${speed.upRates} en débit montant`}
                                >
                                    {' '}
                                    ↓&nbsp;{speed.downRates.replace(' ', '\u00A0')}&nbsp;&nbsp;↑&nbsp;
                                    {speed.upRates.replace(' ', '\u00A0')}
                                </Text>
                            </>
                        )}
                    </>
                ),
            }))}
        />
    );
}
